import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import ClientLayout from '../../../components/layout/ClientLayout';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Spinner from '../../../components/ui/Spinner';
import Alert from '../../../components/ui/Alert';
import Card from '../../../components/ui/Card';
import { useAuth } from '../../../hooks/useAuth';
import { useFirestoreQuery } from '../../../hooks/useFirestoreQuery';
import { useFirestoreMutation } from '../../../hooks/useFirestoreMutation';
import { ICliente as Cliente } from '../../../types/firestore.types';
import { Timestamp } from 'firebase/firestore';

// Esquema de validação para o perfil do cliente
const clienteProfileSchema = z.object({
  nome: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres.').max(255, 'Nome não pode exceder 255 caracteres.'),
  telefone: z.string().optional().nullable().refine(
    (val) => !val || /^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(val),
    'Telefone inválido. Use o formato (XX) XXXXX-XXXX ou (XX) XXXX-XXXX.'
  ),
  email: z.string().email('Email inválido.').max(255, 'Email não pode exceder 255 caracteres.').optional().nullable(),
});

type ClienteProfileFormData = z.infer<typeof clienteProfileSchema>;

const ProfilePage: React.FC = () => {
  const { usuario } = useAuth();

  const [formData, setFormData] = useState<ClienteProfileFormData>({
    nome: '',
    telefone: '',
    email: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formErrors, setFormErrors] = useState<z.ZodIssue[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  const {
    dados: clienteData,
    carregando: carregandoCliente,
    erro: erroCarregarCliente,
  } = useFirestoreQuery<Cliente>(
    'clientes',
    usuario?.id ? [{ campo: 'id', operador: '==', valor: usuario.id }] : null,
    null,
    1
  );

  const {
    executarMutacao: executarAtualizacaoCliente,
    carregando: salvandoCliente,
    erro: erroSalvarCliente,
  } = useFirestoreMutation('clientes');

  useEffect(() => {
    if (clienteData && clienteData.length > 0) {
      const cliente = clienteData[0];
      setFormData({
        nome: cliente.nome,
        telefone: cliente.telefone || '',
        email: cliente.email || '',
      });
    }
  }, [clienteData]);

  useEffect(() => {
    if (erroSalvarCliente) {
      setAlertMessage(`Erro ao salvar perfil: ${erroSalvarCliente.message}`);
      setAlertType('error');
      setShowAlert(true);
    }
  }, [erroSalvarCliente]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors([]);
    setShowAlert(false);

    try {
      clienteProfileSchema.parse(formData);

      if (!usuario?.id) {
        throw new Error('ID do cliente não encontrado para atualização.');
      }

      const updatedData: Partial<Cliente> = {
        nome: formData.nome,
        telefone: formData.telefone || null,
        email: formData.email || null,
        atualizado_em: Timestamp.now(),
      };

      await executarAtualizacaoCliente(usuario.id, updatedData);

      setAlertMessage('Perfil atualizado com sucesso!');
      setAlertType('success');
      setShowAlert(true);
      setIsEditing(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setFormErrors(error.errors);
        setAlertMessage('Verifique os campos do formulário.');
        setAlertType('warning');
        setShowAlert(true);
      } else if (error instanceof Error) {
        setAlertMessage(`Erro ao salvar perfil: ${error.message}`);
        setAlertType('error');
        setShowAlert(true);
      } else {
        setAlertMessage('Ocorreu um erro inesperado ao salvar o perfil.');
        setAlertType('error');
        setShowAlert(true);
      }
    }
  };

  const handleCancel = () => {
    if (clienteData && clienteData.length > 0) {
      const cliente = clienteData[0];
      setFormData({
        nome: cliente.nome,
        telefone: cliente.telefone || '',
        email: cliente.email || '',
      });
    }
    setFormErrors([]);
    setShowAlert(false);
    setIsEditing(false);
  };

  if (carregandoCliente) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center h-full">
          <Spinner size="lg" color="primary" />
        </div>
      </ClientLayout>
    );
  }

  if (erroCarregarCliente) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center h-full text-danger-500">
          <h2 className="text-2xl font-bold mb-4">Erro ao carregar perfil</h2>
          <p className="text-lg">Não foi possível carregar os dados do seu perfil. Tente novamente mais tarde.</p>
          <p className="text-sm text-gray-400 mt-2">{erroCarregarCliente.message}</p>
        </div>
      </ClientLayout>
    );
  }

  if (!clienteData || clienteData.length === 0) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center h-full text-text-500">
          <h2 className="text-2xl font-bold mb-4">Perfil não encontrado</h2>
          <p className="text-lg">Não foi possível encontrar os dados do seu perfil. Por favor, entre em contato com o suporte.</p>
        </div>
      </ClientLayout>
    );
  }

  const clienteAtual = clienteData[0];

  return (
    <ClientLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center justify-center min-h-full bg-background p-4"
      >
        <Card className="w-full max-w-2xl p-6 sm:p-8 lg:p-10 bg-background-800 shadow-xl rounded-lg border border-primary/20-700">
          <h1 className="text-4xl font-extrabold text-primary-500 mb-8 text-center drop-shadow-lg">
            Meu Perfil
          </h1>

          {showAlert && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6"
            >
              <Alert tipo={alertType} mensagem={alertMessage} onClose={() => setShowAlert(false)} />
            </motion.div>
          )}

          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <label htmlFor="nome" className="block text-text-500 text-sm font-medium mb-2">
                Nome Completo
              </label>
              <Input
                id="nome"
                name="nome"
                type="text"
                value={formData.nome}
                onChange={handleInputChange}
                disabled={!isEditing || salvandoCliente}
                placeholder="Seu nome completo"
                className="w-full bg-background-700 border-primary/20-600 text-text-500 focus:border-primary-500 focus:ring-primary-500"
              />
              {formErrors.find((err) => err.path[0] === 'nome') && (
                <p className="text-danger-500 text-xs mt-1">
                  {formErrors.find((err) => err.path[0] === 'nome')?.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="telefone" className="block text-text-500 text-sm font-medium mb-2">
                Telefone
              </label>
              <Input
                id="telefone"
                name="telefone"
                type="tel"
                value={formData.telefone}
                onChange={handleInputChange}
                disabled={!isEditing || salvandoCliente}
                placeholder="(XX) XXXXX-XXXX"
                className="w-full bg-background-700 border-primary/20-600 text-text-500 focus:border-primary-500 focus:ring-primary-500"
              />
              {formErrors.find((err) => err.path[0] === 'telefone') && (
                <p className="text-danger-500 text-xs mt-1">
                  {formErrors.find((err) => err.path[0] === 'telefone')?.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-text-500 text-sm font-medium mb-2">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!isEditing || salvandoCliente}
                placeholder="seu.email@exemplo.com"
                className="w-full bg-background-700 border-primary/20-600 text-text-500 focus:border-primary-500 focus:ring-primary-500"
              />
              {formErrors.find((err) => err.path[0] === 'email') && (
                <p className="text-danger-500 text-xs mt-1">
                  {formErrors.find((err) => err.path[0] === 'email')?.message}
                </p>
              )}
            </div>

            <div className="flex justify-between items-center pt-4">
              {!isEditing ? (
                <Button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="w-full md:w-auto px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-md shadow-md transition-all duration-300 ease-in-out transform hover:scale-105"
                >
                  Editar Perfil
                </Button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4 w-full">
                  <Button
                    type="submit"
                    disabled={salvandoCliente}
                    className="flex-1 px-6 py-3 bg-success-600 hover:bg-success-700 text-white font-semibold rounded-md shadow-md transition-all duration-300 ease-in-out transform hover:scale-105"
                  >
                    {salvandoCliente ? <Spinner size="sm" color="white" /> : 'Salvar Alterações'}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCancel}
                    disabled={salvandoCliente}
                    className="flex-1 px-6 py-3 bg-danger-600 hover:bg-danger-700 text-white font-semibold rounded-md shadow-md transition-all duration-300 ease-in-out transform hover:scale-105"
                  >
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-primary/20-700 text-text-600 text-sm text-center">
            <p>Membro desde: {clienteAtual.criado_em?.toDate().toLocaleDateString('pt-BR')}</p>
            {clienteAtual.atualizado_em && (
              <p>Última atualização: {clienteAtual.atualizado_em?.toDate().toLocaleDateString('pt-BR')}</p>
            )}
          </div>
        </Card>
      </motion.div>
    </ClientLayout>
  );
};

export default ProfilePage;